import React, { useMemo, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BoltIcon from '@mui/icons-material/Bolt';
import dayjs from 'dayjs';
import {
  BG, MONTH_NAMES, DAY_TH_ABBR,
} from './payroll/constants';
import {
  th0, td0, dayTh, dayTdBase, dayCellInner, weekendCellBg, calcOtForDay,
} from './payroll/utils';

const OT_GREEN = '#16a34a'; // OT วันนั้น >= 4 ชม.
const OT_AMBER = '#d97706'; // OT วันนั้นมีแต่ยังไม่ถึง 4 ชม.
const EMPTY_DASH = <span style={{ color: '#d1d5db' }}>-</span>;

// pill tab สไตล์เดียวกับ "พนักงานรายวัน/พนักงานรายเดือน" ใน HrReportV2.js (TYPE_TABS) — gradient
// ตอน active + ไอคอนนำหน้า แทน MUI Tabs ธรรมดาเดิม
const VIEW_TABS = [
  { value: 'attendance', label: 'ลงเวลา', icon: AccessTimeIcon, gradient: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)' },
  { value: 'ot', label: 'โอที', icon: BoltIcon, gradient: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' },
];

// ตารางสรุป "โอที"/"ลงเวลา" — read-only, ไม่มี dialog แก้ไข (ต่างจาก MergedPayrollTableV2 ที่แก้ไขได้
// เต็มรูปแบบ) รับ timestamp/manualWorkData/days/month/year ตรงจาก HrReportV2.js เป็น props ตรงๆ
// ไม่ dispatch/fetch เอง — ใช้ period (วันในช่วง) ที่ parent เลือกอยู่แล้วเป๊ะ ไม่คำนวณครึ่งเดือนเอง
// reuse style helper/สี/คำนวณ OT (calcOtForDay) จาก components/Table/payroll/ ตัวเดียวกับตารางเงินเดือนหลัก
// กันตัวเลข OT เพี้ยนจากกัน (single source of truth เดิม)
export function OtTimestampSummaryTable({
  timestamp, manualWorkData = [], days = [], month, year, holidayDaySet,
}) {
  const [tab, setTab] = useState('attendance'); // 'attendance' | 'ot'
  const isOt = tab === 'ot';

  const dayMeta = useMemo(() => days.map((day) => {
    const dow = new Date(year, month, day).getDay();
    return { day, dow, isHoliday: (holidayDaySet || new Set()).has(day) };
  }), [days, year, month, holidayDaySet]);

  // O(1) lookup ต่อ empId+day — pattern เดียวกับ checkinByKey ใน MergedPayrollTableV2.js
  // (checkin จริง is_manual=false ชนะเสมอถ้ามีซ้ำ)
  const checkinByKey = useMemo(() => {
    const map = {};
    (timestamp?.rows || []).forEach((emp) => {
      const empId = String(emp.employee_id);
      (emp.checkins || []).forEach((c) => {
        if (!c?.checkIn) return;
        const d = dayjs(c.checkIn);
        if (d.year() !== year || d.month() !== month) return;
        const key = `${empId}-${d.date()}`;
        const existing = map[key];
        if (!(existing && !existing.is_manual && c.is_manual)) map[key] = c;
      });
    });
    return map;
  }, [timestamp, year, month]);

  const manualByKey = useMemo(() => {
    const map = {};
    manualWorkData.forEach((e) => {
      const d = new Date(e.date);
      if (d.getUTCFullYear() !== year || d.getUTCMonth() + 1 !== month + 1) return;
      map[`${e.employee_id}-${d.getUTCDate()}`] = e;
    });
    return map;
  }, [manualWorkData, year, month]);

  const rows = timestamp?.rows || [];

  // ต่อ 1 แถวพนักงาน: ค่าต่อวัน (สำหรับโชว์ cell) + ยอดสรุป (สรุปวัน/สรุปชม./วัน OT>=4ชม.)
  const rowData = useMemo(() => rows.map((emp) => {
    const empId = String(emp.employee_id);
    const dayHrRate = (emp.salary_per_day || 0) / 8;
    let sumDays = 0;
    let sumHours = 0;
    let sumOver4 = 0;
    const dayValues = dayMeta.map(({ day }) => {
      const checkin = checkinByKey[`${empId}-${day}`] || null;
      if (!isOt) {
        const hours = checkin ? (checkin.totalHour || 0) : 0;
        if (hours > 0) { sumDays += 1; sumHours += hours; }
        return hours > 0 ? hours : null;
      }
      const manualEntry = manualByKey[`${empId}-${day}`] || null;
      const { otHours } = calcOtForDay(checkin, manualEntry, dayHrRate);
      if (otHours > 0) {
        sumDays += 1;
        sumHours += otHours;
        if (otHours >= 4) sumOver4 += 1;
      }
      return otHours > 0 ? otHours : null;
    });
    return {
      empId, name: emp.name, dayValues, sumDays, sumHours, sumOver4,
    };
  }), [rows, dayMeta, checkinByKey, manualByKey, isOt]);

  if (!rows.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">ไม่มีข้อมูลพนักงานในช่วงนี้</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', width: '100%', height: '100%', minHeight: 0, minWidth: 0,
    }}
    >
      <Paper
        elevation={0}
        sx={{
          alignSelf: 'center',
          display: 'flex',
          gap: 0.5,
          p: 0.5,
          mb: 1.5,
          flexShrink: 0,
          borderRadius: '14px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {VIEW_TABS.map((t) => {
          const selected = tab === t.value;
          const Icon = t.icon;
          return (
            <Box
              key={t.value}
              onClick={() => setTab(t.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.75,
                py: 0.75,
                borderRadius: '10px',
                cursor: 'pointer',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
                transform: selected ? 'translateY(-1px)' : 'none',
                background: selected ? t.gradient : 'transparent',
                boxShadow: selected ? '0 6px 16px -4px rgba(0,0,0,0.35)' : 'none',
                '&:hover': {
                  background: selected ? t.gradient : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.04)'),
                },
              }}
            >
              <Icon sx={{ fontSize: 17, color: selected ? '#fff' : 'text.secondary' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 500, color: selected ? '#fff' : 'text.secondary' }}>
                {t.label}
              </Typography>
            </Box>
          );
        })}
      </Paper>
      <Box sx={{
        flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto',
      }}
      >
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th colSpan={2} style={th0({ position: 'sticky', left: 0, top: 0, zIndex: 50, background: BG.hInfo })}>
                ข้อมูลพนักงาน
              </th>
              <th colSpan={dayMeta.length} style={th0({ position: 'sticky', top: 0, zIndex: 40, background: BG.hDays })}>
                {isOt ? 'โอที' : 'การลงเวลา'} — {MONTH_NAMES[month]} {year}
              </th>
              <th colSpan={isOt ? 3 : 2} style={th0({ position: 'sticky', top: 0, zIndex: 40, background: BG.hSum, borderRight: 'none' })}>
                สรุป
              </th>
            </tr>
            <tr>
              <th style={th0({ position: 'sticky', left: 0, top: 34, zIndex: 50, background: BG.cInfo, minWidth: 38 })}>No.</th>
              <th style={th0({ position: 'sticky', left: 38, top: 34, zIndex: 50, background: BG.cInfo, minWidth: 180, textAlign: 'left', paddingLeft: 12 })}>
                ชื่อ - สกุล
              </th>
              {dayMeta.map(({ day, dow, isHoliday }) => (
                <th key={day} style={dayTh(dow, false, isHoliday)}>
                  <div style={{ fontSize: 11, color: BG.textMuted, marginBottom: 4 }}>{DAY_TH_ABBR[dow]}.</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: isHoliday || dow === 0 ? BG.weekendText : BG.headerText }}>{day}</div>
                </th>
              ))}
              <th style={th0({ position: 'sticky', top: 34, zIndex: 30, background: BG.cSum, minWidth: 72 })}>สรุปวัน</th>
              <th style={th0({ position: 'sticky', top: 34, zIndex: 30, background: BG.cSum, minWidth: 72 })}>สรุปชม.</th>
              {isOt && (
                <th style={th0({ position: 'sticky', top: 34, zIndex: 30, background: BG.cSum, minWidth: 100, borderRight: 'none' })}>
                  วัน OT ≥ 4 ชม.
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rowData.map((r, idx) => {
              const rowBg = idx % 2 === 1 ? BG.rowOdd : BG.rowEven;
              return (
                <tr key={r.empId}>
                  <td style={td0({ position: 'sticky', left: 0, zIndex: 20, background: rowBg })}>{idx + 1}</td>
                  <td style={td0({
                    position: 'sticky', left: 38, zIndex: 20, background: rowBg, textAlign: 'left', paddingLeft: 12, whiteSpace: 'nowrap',
                  })}
                  >
                    {r.name}
                  </td>
                  {r.dayValues.map((v, i) => {
                    const { dow, isHoliday } = dayMeta[i];
                    return (
                      <td key={dayMeta[i].day} style={dayTdBase({ background: weekendCellBg(dow, isHoliday) })}>
                        <div style={dayCellInner({
                          color: isOt && v != null ? (v >= 4 ? OT_GREEN : OT_AMBER) : undefined,
                          fontWeight: isOt && v != null ? 700 : 400,
                        })}
                        >
                          {v != null ? parseFloat(v.toFixed(2)) : EMPTY_DASH}
                        </div>
                      </td>
                    );
                  })}
                  <td style={td0()}>{r.sumDays}</td>
                  <td style={td0()}>{parseFloat(r.sumHours.toFixed(2))}</td>
                  {isOt && (
                    <td style={td0({ borderRight: 'none', color: r.sumOver4 > 0 ? OT_GREEN : undefined, fontWeight: r.sumOver4 > 0 ? 700 : 400 })}>
                      {r.sumOver4}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}

export default OtTimestampSummaryTable;
